/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useState } from "react";
import { useSelector } from "store";

import { ThumbnailPreview } from "./ThumbnailPreview";
import { generatePlaceholderImage } from "./placeholderImage";
import { useVideoGroupsSet } from "./useVideoGroupsSet";

const CloseButton = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M15 5L5 15"
        stroke="white"
        strokeOpacity="0.7"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 5L15 15"
        stroke="white"
        strokeOpacity="0.7"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const MultiAngleSwitcherContainer: React.FC<{
  handleSwitchMultiAngleVideo: (videoId: string) => void;
}> = ({ handleSwitchMultiAngleVideo }) => {
  const streamingKey = useSelector((state) => state.streaming.streamingKey);
  const eventVideoMap = useSelector((state) => state.streaming.eventVideoMap);

  const currentVideoId = useSelector((state) => state.streaming.currentVideoId);

  const [isMultiAngleViewOpen, setIsMultiAngleViewOpen] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const videoGroupsSet = useVideoGroupsSet(currentVideoId);

  if (!currentVideoId) {
    return null;
  }

  if (videoGroupsSet === undefined) {
    return null;
  }

  /**
   * 後から実装時に使う予定
   */
  const _currentCookie = streamingKey?.cookies?.[currentVideoId]?.default;
  const _currentVideo = eventVideoMap?.[currentVideoId];

  if (!isMultiAngleViewOpen) {
    return (
      <div
        style={{
          position: "absolute",
          bottom: "92px",
          right: "8px",
          zIndex: 9999,
          color: "white",
        }}
      >
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            borderRadius: "12px",
            backgroundColor: "#00000042",
          }}
          onClick={() => {
            setIsMultiAngleViewOpen(true);
          }}
          type="button"
        >
          <span
            style={{
              fontSize: 14,
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "20px",
            }}
          >
            カメラ切り替え
          </span>
          <img
            src={generatePlaceholderImage("カメラ切り替え")}
            alt="Camera Switch Icon"
            width="60px"
            height="34px"
            style={{
              borderRadius: "8px",
              border: "3px solid #fff",
            }}
          />
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        paddingTop: "24px",
        paddingLeft: "24px",
        paddingRight: "24px",
        paddingBottom: "32px",
        bottom: "92px",
        left: "8px",
        zIndex: 9999,
        color: "black",
        width: "calc(100% - 16px)",
        borderRadius: "8px",
        // RTX VSR が非アクティブになるため、backdrop-filter は使用しない。できるだけ見た目を近づけている
        backgroundColor: "rgba(0, 0, 0, 0.55)",
        backgroundImage:
          "linear-gradient(to bottom, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0) 35%, rgba(0, 0, 0, 0.1))",
        border: "1px solid rgba(255, 255, 255, 0.07)",
        boxShadow:
          "0 10px 24px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <h3
          style={{
            color: "white",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "24px",
          }}
        >
          カメラ切り替え
        </h3>
        <button onClick={() => setIsMultiAngleViewOpen(false)}>
          <CloseButton />
        </button>
      </div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        {videoGroupsSet.videoGroups.map((group, index) => (
          <button
            key={group.name}
            onClick={() => setSelectedTabIndex(index)}
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              lineHeight: "20px",
              padding: "4px 16px",
              borderRadius: "9999px",
              color: selectedTabIndex === index ? "black" : "white",
              backgroundColor: selectedTabIndex === index ? "white" : "#fff3",
              border: "none",
              cursor: "pointer",
            }}
          >
            {group.name}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: "16px", overflowX: "auto" }}>
        {videoGroupsSet.videoGroups[selectedTabIndex]?.videos.map((video) => {
          const cookie = streamingKey?.cookies?.[video.videoId]?.default;

          if (cookie === undefined) {
            return null;
          }

          return (
            <ThumbnailPreview
              key={video.videoId}
              video={video}
              cookie={cookie}
              /**
               * TODO: 再生中の映像かどうかの判定ロジックを実装
               */
              isCurrentVideo={false}
              handleSwitchMultiAngleVideo={handleSwitchMultiAngleVideo}
            />
          );
        })}
      </div>
    </div>
  );
};
